import { MouseEvent, useCallback, useState } from 'react';

import Analytics from '@hh.ru/analytics-js';
import { HSpacing, Link, Loader } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import markContactsAsShown from 'src/api/resume/markContactsAsShown.obfuscate';
import { useNotification } from 'src/components/Notifications/Provider';
import resumeContactsErrorNotification from 'src/components/Notifications/ResumeContactsError';
import VerticallyCentredIconSize16 from 'src/components/VerticallyCentredIconSize16';
import translation from 'src/components/translation';
import type { PreferredContact } from 'src/models/resume/resumeCommon.types';
import type { Phone } from 'src/models/resumeCard';

import ContactsPhone from 'src/components/ResumeCard/Body/Contacts/Phone';

const TrlKeys = {
    show: 'resume.search.phone.show',
};

interface Props {
    phone: Phone;
    preferredPhone?: PreferredContact;
    openContacts: () => void;
    markAsRead?: () => void;
    resumeId: string;
    resumeHash: string;
    goal?: string;
    vacancyId: string;
}

const ContactsClosed: TranslatedComponent<Props> = ({
    phone,
    preferredPhone,
    openContacts,
    markAsRead,
    resumeId,
    resumeHash,
    goal,
    vacancyId,
    trls,
}) => {
    const { addNotification } = useNotification();

    const [isLoading, setLoading] = useState(false);

    const handleClick = useCallback(
        async (event: MouseEvent) => {
            setLoading(true);
            try {
                await markContactsAsShown({ event, resumeId, resumeHash, goal });
            } catch (_) {
                addNotification(resumeContactsErrorNotification);
                return;
            } finally {
                setLoading(false);
            }
            openContacts();
            markAsRead?.();
            Analytics.sendHHEvent('resumeShowContacts', { goal: 'ShowContacts', resumeId, vacancyId });
        },
        [addNotification, goal, markAsRead, resumeHash, resumeId, openContacts, vacancyId]
    );

    return (
        <span>
            <ContactsPhone phone={phone} preferredPhone={preferredPhone} />
            <HSpacing default={8} />
            <Link
                inline
                typography="label-3-regular"
                onClick={handleClick}
                disabled={isLoading}
                data-qa="response-resume_show-phone-number"
            >
                {trls[TrlKeys.show]}
            </Link>
            {isLoading && (
                <>
                    <HSpacing default={8} />
                    <VerticallyCentredIconSize16 icon={<Loader size={16} />} />
                </>
            )}
        </span>
    );
};

export default translation(ContactsClosed);
